import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ExternalLink, Info, ShieldCheck, Sparkles, RefreshCw } from "lucide-react";
import { Gate } from "@/components/wallet/Gate";
import { XrpLogo } from "@/components/wallet/XrpLogo";
import { useWallet } from "@/lib/wallet/store";
import { overviewQuery, priceQuery } from "@/lib/wallet/queries";
import { getOnrampQuotes } from "@/lib/wallet/markets.functions";
import type { OnrampProviderId } from "@/lib/wallet/onramp-types";
import { addressFingerprint, formatAmount, truncateAddress } from "@/lib/wallet/format";
import {
  buildMoonpayUrl,
  buildProviderUrl,
  DIRECT_DELIVERY,
  isOnrampConfigured,
  isSandboxKey,
  MAX_USD,
  MIN_USD,
  PRESET_USD,
} from "@/lib/wallet/onramp";

export const Route = createFileRoute("/buy")({
  staticData: { sitemap: false },
  head: () => ({
    meta: [
      { title: "Buy XRP with USD — Cobalt Wallet" },
      {
        name: "description",
        content:
          "Buy XRP with USD by card or bank transfer and have it delivered straight to your own XRP Ledger address. Cobalt never holds your funds.",
      },
      { property: "og:title", content: "Buy XRP with USD — Cobalt Wallet" },
      {
        property: "og:description",
        content: "Fiat on-ramp that delivers XRP directly to your self-custodied XRPL address.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: () => (
    <Gate>
      <BuyScreen />
    </Gate>
  ),
});

function BuyScreen() {
  const { address, settings } = useWallet();
  const overview = useQuery(overviewQuery(address));
  const price = useQuery(priceQuery(settings.fiatLookup));
  const [usd, setUsd] = useState("250");

  const amount = Number(usd) || 0;
  const xrpUsd = price.data ?? null;
  const estXrp = xrpUsd && amount > 0 ? amount / xrpUsd : null;
  const tooLow = amount > 0 && amount < MIN_USD;
  const tooHigh = amount > MAX_USD;
  const valid = amount >= MIN_USD && amount <= MAX_USD;

  // Debounce the amount so keystrokes don't hammer the rate providers.
  const [quoteAmount, setQuoteAmount] = useState(250);
  useEffect(() => {
    const timer = setTimeout(() => setQuoteAmount(amount >= MIN_USD ? amount : MIN_USD), 350);
    return () => clearTimeout(timer);
  }, [amount]);

  const quotes = useQuery({
    queryKey: ["onramp", "quotes", quoteAmount],
    queryFn: () => getOnrampQuotes({ data: { usdAmount: quoteAmount } }),
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  const [chosen, setChosen] = useState<OnrampProviderId | null>(null);
  const best = quotes.data?.quotes[0]?.id ?? null;
  const selected = chosen ?? best ?? "moonpay";
  const setSelected = (id: OnrampProviderId) => setChosen(id);
  const activeQuote = quotes.data?.quotes.find((q) => q.id === selected) ?? null;

  const activeUrl = useMemo(() => {
    if (!valid || !address) return null;
    if (selected === "moonpay") {
      return buildMoonpayUrl({
        address,
        usdAmount: amount,
        ...(typeof window !== "undefined" ? { redirectUrl: `${window.location.origin}/` } : {}),
      });
    }
    return buildProviderUrl(selected, address, amount);
  }, [valid, address, amount, selected]);

  return (
    <div className="animate-rise space-y-4">
      <div className="flex items-center gap-2">
        <Link
          to="/"
          aria-label="Back to home"
          className="grid size-8 place-items-center rounded-lg border border-border text-muted"
        >
          <ChevronLeft className="size-4" />
        </Link>
        <h1 className="text-[18px] font-medium tracking-tight">Buy XRP</h1>
      </div>

      <section className="panel space-y-4 p-4">
        <div>
          <p className="label-instrument">You pay (USD)</p>
          <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-border bg-background px-3">
            <span className="readout text-[15px] text-faint">$</span>
            <input
              value={usd}
              onChange={(e) => setUsd(e.target.value.replace(/[^0-9.]/g, ""))}
              inputMode="decimal"
              placeholder="250"
              className="readout h-12 w-full bg-transparent text-[20px] outline-none placeholder:text-faint"
            />
            <span className="readout text-[12px] text-muted">USD</span>
          </div>
          <div className="mt-2 grid grid-cols-4 gap-2">
            {PRESET_USD.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setUsd(String(preset))}
                className={`readout h-9 rounded-lg border text-[11px] ${
                  amount === preset ? "chip-active" : "border-border bg-background text-muted"
                }`}
              >
                ${preset}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border bg-background p-3">
          <div className="flex items-center gap-2.5">
            <span className="grid size-8 shrink-0 place-items-center rounded-full bg-elevated p-1.5">
              <XrpLogo className="size-full" />
            </span>
            <div>
              <p className="label-instrument">You receive (estimate)</p>
              <p className="readout text-[15px]">
                {estXrp !== null ? `${formatAmount(estXrp, true)} XRP` : "—"}
              </p>
            </div>
          </div>
          <p className="readout text-right text-[10px] text-faint">
            {xrpUsd ? `1 XRP ≈ $${xrpUsd.toFixed(4)}` : "price unavailable"}
            <br />
            before provider fees
          </p>
        </div>

        <div className="rounded-lg border border-border bg-background p-3">
          <p className="label-instrument">Delivery address</p>
          <p className="readout mt-1 text-[12px]">{truncateAddress(address ?? "", 8, 8)}</p>
          <p className="readout mt-1 flex items-center gap-1.5 text-[10px] text-success">
            <ShieldCheck className="size-3" /> Fingerprint {addressFingerprint(address ?? "")} · your
            keys, your XRP
          </p>
        </div>

        {tooLow ? (
          <p className="readout text-[10px] text-danger">Minimum purchase is ${MIN_USD}.</p>
        ) : tooHigh ? (
          <p className="readout text-[10px] text-danger">
            Maximum per purchase is ${MAX_USD.toLocaleString()}.
          </p>
        ) : null}

        {overview.data && !overview.data.funded ? (
          <p className="readout rounded-lg border border-accent/40 bg-accent/10 p-2.5 text-[10px] leading-relaxed text-accent">
            This account is not activated yet. Buy at least {formatAmount(overview.data.baseReserve)}{" "}
            XRP so the delivery funds the reserve and opens the account.
          </p>
        ) : null}

        <a
          href={activeUrl ?? undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!activeUrl}
          className={`readout flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-accent text-[11px] uppercase tracking-[0.15em] text-accent-ink ${
            activeUrl ? "" : "pointer-events-none opacity-40"
          }`}
        >
          Buy with {activeQuote?.name ?? "MoonPay"} <ExternalLink className="size-3.5" />
        </a>

        {isOnrampConfigured && isSandboxKey && selected === "moonpay" ? (
          <p className="readout rounded-lg border border-accent/40 bg-accent/10 p-2.5 text-[10px] text-accent">
            Sandbox mode — test cards only, no real money and no real XRP is delivered.
          </p>
        ) : activeQuote && !DIRECT_DELIVERY[activeQuote.id] ? (
          <p className="readout rounded-lg border border-border bg-background p-2.5 text-[10px] leading-relaxed text-faint">
            {activeQuote.name} settles in your {activeQuote.name} account first — withdraw XRP to the
            address above (tap Receive to copy it) and it lands in this wallet.
          </p>
        ) : (
          <p className="readout rounded-lg border border-border bg-background p-2.5 text-[10px] leading-relaxed text-faint">
            You'll sign in to your own MoonPay account to pay. Your Cobalt address is pre-filled as
            the delivery destination, so purchased XRP lands straight in this wallet.
          </p>
        )}
      </section>

      <section className="panel p-4">
        <div className="flex items-center justify-between gap-2">
          <p className="readout text-[11px] uppercase tracking-[0.15em] text-muted">Best buy rate</p>
          <button
            type="button"
            onClick={() => quotes.refetch()}
            className="readout flex items-center gap-1 text-[10px] text-faint transition-colors hover:text-foreground"
          >
            <RefreshCw className={`size-3 ${quotes.isFetching ? "animate-spin" : ""}`} /> refresh
          </button>
        </div>

        <div className="mt-3 space-y-2">
          {quotes.isLoading
            ? [0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-lg border border-border bg-background" />
              ))
            : quotes.data?.quotes.map((quote, index) => {
                const isBest = index === 0 && quote.xrpOut !== null;
                const isSelected = selected === quote.id;
                return (
                  <button
                    key={quote.id}
                    type="button"
                    onClick={() => setSelected(quote.id)}
                    className={`flex w-full items-center justify-between gap-3 rounded-lg border p-3 text-left transition-colors ${
                      isSelected ? "border-accent bg-accent/10" : "border-border bg-background"
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-[12px] font-medium">
                        {quote.name}
                        {isBest ? (
                          <span className="readout flex items-center gap-1 rounded-full bg-accent/15 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-accent">
                            <Sparkles className="size-2.5" /> best
                          </span>
                        ) : null}
                        {DIRECT_DELIVERY[quote.id] ? (
                          <span className="readout rounded-full border border-border px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-faint">
                            direct
                          </span>
                        ) : null}
                      </p>
                      <p className="readout mt-0.5 truncate text-[10px] text-faint">{quote.feeNote}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="readout text-[13px]">
                        {quote.xrpOut !== null ? `${formatAmount(quote.xrpOut, true)} XRP` : "—"}
                      </p>
                      <p className="readout text-[9px] text-faint">
                        {quote.spot ? `@ $${quote.spot.toFixed(4)}` : "rate unavailable"}
                      </p>
                    </div>
                  </button>
                );
              })}
        </div>

        {quotes.data ? (
          <p className="readout mt-2.5 text-[9px] text-faint">
            Estimated for ${quotes.data.usdAmount.toLocaleString()} · updated{" "}
            {new Date(quotes.data.fetchedAt).toLocaleTimeString()}
          </p>
        ) : null}

        <p className="readout mt-3 flex items-start gap-1.5 text-[10px] leading-relaxed text-faint">
          <Info className="mt-0.5 size-3 shrink-0" />
          <span>
            Rates come from each venue's public price feed with their published retail fees applied —
            final pricing is whatever the provider quotes at checkout. Cobalt charges no
            on-ramp fee, never sees your card details, and only shares your public XRPL address.
            Purchased XRP appears on the Home screen once the ledger settles the delivery.
          </span>
        </p>
      </section>
    </div>
  );
}
